import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getSeo,
  SITE_ORIGIN,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_WIDTH,
  DEFAULT_OG_HEIGHT,
} from "../../data/seoMap";

/**
 * Zero-config SEO head manager. Looks up the current pathname against the
 * central seoMap and *updates* (never duplicates) the pre-rendered head tags.
 *
 * The same data is baked into raw HTML at build time by scripts/prerender.ts,
 * so on first paint crawlers see correct tags. This component then updates
 * those tags on client-side navigation between routes.
 */
export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = getSeo(pathname);
    const url = `${SITE_ORIGIN}${meta.path === "/" ? "/" : meta.path}`;
    const ogImage = meta.ogImage || DEFAULT_OG_IMAGE;
    const robots = meta.noindex ? "noindex, follow" : "index, follow, max-image-preview:large";
    const ogTitle = meta.ogTitle || meta.title;

    document.title = meta.title;

    setMetaByName("description", meta.description);
    setMetaByName("robots", robots);
    setLinkByRel("canonical", url);

    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:title", ogTitle);
    setMetaByProperty("og:description", meta.description);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", ogImage);
    setMetaByProperty("og:image:width", String(DEFAULT_OG_WIDTH));
    setMetaByProperty("og:image:height", String(DEFAULT_OG_HEIGHT));
    setMetaByProperty("og:locale", "en_AU");

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", ogTitle);
    setMetaByName("twitter:description", meta.description);
    setMetaByName("twitter:image", ogImage);

    // Page-specific JSON-LD: tag with data-seo so we replace the prerendered one
    // and never duplicate. Site-wide LocalBusiness/WebSite live in index.html and
    // do not carry data-seo, so we never touch them.
    const existingPageLd = document.head.querySelector('script[type="application/ld+json"][data-seo="page"]');
    if (existingPageLd) existingPageLd.remove();
    if (meta.jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      script.text = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
}

function setMetaByName(name, content) {
  upsertHeadElement(`meta[name="${name}"]`, () => {
    const el = document.createElement("meta");
    el.setAttribute("name", name);
    return el;
  }, (el) => {
    el.setAttribute("content", content);
  });
}

function setMetaByProperty(property, content) {
  upsertHeadElement(`meta[property="${property}"]`, () => {
    const el = document.createElement("meta");
    el.setAttribute("property", property);
    return el;
  }, (el) => {
    el.setAttribute("content", content);
  });
}

function setLinkByRel(rel, href) {
  upsertHeadElement(`link[rel="${rel}"]`, () => {
    const el = document.createElement("link");
    el.setAttribute("rel", rel);
    return el;
  }, (el) => {
    el.setAttribute("href", href);
  });
}

function upsertHeadElement(selector, create, update) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  update(el);
}
